/**
 * usePeerRequests — запросы peer-review («кто о ком пишет отчёт») за день.
 * GET /api/peer-review/requests?date= → пары author→subject, сгруппированные
 * по booking_id. Используется в OO-расстановке, чтобы показать связи прямо в
 * мини-карточках.
 * 13.10 (владелец: «кнопка „друг о друге“ автоматически сохраняется, без нажатия на кнопку „сохранить“»):
 * правки пар — черновик доски, как назначения. add/remove меняют только экран (пара видна сразу), на сервер
 * уходит разница «экран − сервер» по «Сохранить»/«Отправить» (flush из doPublish); dirty зажигает «!» на
 * галочке. Смена дня черновик пар сбрасывает — как и несохранённую расстановку.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "../api/client";
import type { ApiList } from "../api/types";
import { fmtDateApi } from "../utils/date";

export interface PeerPair {
  authorId: string;
  authorName: string;
  subjectId: string | null;
  subjectName: string | null;
}

interface RawPeer {
  booking_id: number | string;
  author_id: string;
  subject_id?: string | null;
  author_name?: string | null;
  subject_name?: string | null;
}

/** One author→subject request to create; имена — для мгновенного показа до сохранения. */
export interface PeerReqItem { author_id: string; subject_id: string | null; author_name?: string | null; subject_name?: string | null }

type Op = { kind: "add"; bookingId: string; req: PeerReqItem } | { kind: "remove"; bookingId: string; authorId: string; subjectId: string | null };

const keyOf = (bookingId: string, authorId: string, subjectId: string | null | undefined) => `${bookingId}|${authorId}|${subjectId ?? ""}`;
const rowKey = (r: RawPeer) => keyOf(String(r.booking_id), String(r.author_id), r.subject_id ? String(r.subject_id) : null);

export function usePeerRequests(date: Date) {
  const [server, setServer] = useState<RawPeer[]>([]);
  const [ops, setOps] = useState<Op[]>([]);
  const [trigger, setTrigger] = useState(0);
  const reload = useCallback(() => setTrigger((n) => n + 1), []);

  useEffect(() => { setOps([]); }, [date]);
  useEffect(() => {
    let alive = true;
    api<ApiList<RawPeer>>(`/api/peer-review/requests?date=${fmtDateApi(date)}`)
      .then((r) => { if (alive) setServer(r.results ?? []); })
      .catch(() => { if (alive) setServer([]); });
    return () => { alive = false; };
  }, [date, trigger]);

  /** Экран = серверные пары + несохранённые правки по порядку (повтор пары не дублируется). */
  const rows = useMemo(() => {
    let rs = server;
    for (const op of ops) {
      if (op.kind === "add") {
        const k = keyOf(op.bookingId, op.req.author_id, op.req.subject_id);
        if (!rs.some((r) => rowKey(r) === k)) {
          rs = [...rs, { booking_id: op.bookingId, author_id: op.req.author_id, subject_id: op.req.subject_id, author_name: op.req.author_name ?? null, subject_name: op.req.subject_name ?? null }];
        }
      } else {
        const k = keyOf(op.bookingId, op.authorId, op.subjectId);
        rs = rs.filter((r) => rowKey(r) !== k);
      }
    }
    return rs;
  }, [server, ops]);

  /** bookingId → pairs. */
  const byBooking = useMemo(() => {
    const map: Record<string, PeerPair[]> = {};
    for (const r of rows) {
      const bid = String(r.booking_id);
      (map[bid] ??= []).push({
        authorId: String(r.author_id),
        authorName: r.author_name ?? String(r.author_id),
        subjectId: r.subject_id ? String(r.subject_id) : null,
        subjectName: r.subject_name ?? (r.subject_id ? String(r.subject_id) : null),
      });
    }
    return map;
  }, [rows]);

  /** Что сохранить: пары, которых нет на сервере, и серверные, снятые на экране. */
  const diff = useMemo(() => {
    const have = new Set(server.map(rowKey)), want = new Set(rows.map(rowKey));
    return { adds: rows.filter((r) => !have.has(rowKey(r))), dels: server.filter((r) => !want.has(rowKey(r))) };
  }, [server, rows]);
  const dirty = diff.adds.length > 0 || diff.dels.length > 0;

  const add = useCallback((bookingId: string, requests: PeerReqItem[]) => {
    if (requests.length) setOps((o) => [...o, ...requests.map((req): Op => ({ kind: "add", bookingId, req }))]);
  }, []);
  const remove = useCallback((bookingId: string, authorId: string, subjectId: string | null) => {
    setOps((o) => [...o, { kind: "remove", bookingId, authorId, subjectId }]);
  }, []);

  /** «Сохранить»/«Отправить»: записать разницу. false — не всё записалось, черновик остаётся (повтор безопасен:
      сервер повторную пару игнорирует, снятие — идемпотентно). */
  const flush = useCallback(async (): Promise<boolean> => {
    if (!dirty) return true;
    const byB = new Map<string, { author_id: string; subject_id: string | null }[]>();
    for (const r of diff.adds) {
      const list = byB.get(String(r.booking_id)) ?? [];
      list.push({ author_id: String(r.author_id), subject_id: r.subject_id ? String(r.subject_id) : null });
      byB.set(String(r.booking_id), list);
    }
    try {
      for (const [bookingId, requests] of byB) {
        await api("/api/peer-review/request", { method: "POST", body: { booking_id: bookingId, date: fmtDateApi(date), requests } });
      }
      for (const r of diff.dels) {
        await api("/api/peer-review/unrequest", { method: "POST", body: { booking_id: String(r.booking_id), author_id: String(r.author_id), subject_id: r.subject_id ? String(r.subject_id) : null } });
      }
      setServer(rows); setOps([]);   // экран не мигает до ответа GET
      reload();
      return true;
    } catch {
      reload();
      return false;
    }
  }, [dirty, diff, rows, date, reload]);

  return { byBooking, reload, add, remove, dirty, flush };
}
